"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CANDIDATE_BULK_MAX = exports.candidateBulkSchema = exports.CANDIDATE_BULK_ACTIONS = exports.candidateTagSchema = exports.FACET_LONG_LIST = exports.FACET_SHORT_LIST = exports.candidateFacetsSchema = exports.candidateBrowseSchema = exports.BROWSE_SORT_LABELS = exports.BROWSE_SORTS = exports.EXPERIENCE_BAND_VALUES = exports.BROWSE_STAGE_LABELS = exports.FINAL_DECISION_WINDOW_DAYS = exports.FINAL_BROWSE_STAGES = exports.IN_PROGRESS_BROWSE_STAGES = exports.BROWSE_STAGES = void 0;
exports.browseStageOf = browseStageOf;
exports.overBudgetPercent = overBudgetPercent;
exports.expectedSalaryWithinBudget = expectedSalaryWithinBudget;
exports.overBudgetLabel = overBudgetLabel;
const zod_1 = require("zod");
const calendars_1 = require("./calendars");
const enums_1 = require("./enums");
const communication_1 = require("./communication");
const pools_1 = require("./pools");
const stage_bar_1 = require("./stage-bar");
const schemas_1 = require("./schemas");
// Redesign Group 1 (DECISIONS-LOG 2026-10-18, ADR 0052): All candidates with Advanced search folded
// in (owner, 2026-10-18 "Merge into one page"), the stage tiles, counts beside every filter option
// (Q3 "All options"), the per-person star and candidate tags, and the salary-over-budget mark (Q2).
// --- Where a person is: the stage tiles and the Stage filter --------------------------------------
/**
 * The tiles of CAND-01, in the stage bar's words (§L33). Ranking is a live application at `new`;
 * Review, Shortlist and Contact are steps inside `screening` (`stageBarKeyOf`); Interview is any
 * round. Selected and Rejected are the final decisions of the last 30 days.
 */
exports.BROWSE_STAGES = ['ranking', 'review', 'shortlist', 'contact', 'assessment', 'interview', 'selected', 'rejected'];
exports.IN_PROGRESS_BROWSE_STAGES = ['ranking', 'review', 'shortlist', 'contact', 'assessment', 'interview'];
exports.FINAL_BROWSE_STAGES = ['selected', 'rejected'];
/** The window the final-decision tiles count over. */
exports.FINAL_DECISION_WINDOW_DAYS = 30;
exports.BROWSE_STAGE_LABELS = {
    ranking: 'Automated ranking',
    review: 'Review',
    shortlist: 'Shortlist',
    contact: 'Contact',
    assessment: 'Assessment',
    interview: 'Interview',
    selected: 'Selected',
    rejected: 'Rejected',
};
const DAY_MS = 86_400_000;
const BAR_TO_BROWSE = {
    review: 'review',
    shortlist: 'shortlist',
    contact: 'contact',
    assessment: 'assessment',
    interview_1: 'interview',
    interview_2: 'interview',
    interview_3: 'interview',
    selected: 'selected',
};
/**
 * Which tile an application counts on, or null: on hold, out, a manager's No waiting for HR
 * (§L31), or a final decision older than the window. Ranking is a live `new`; the rest follow
 * the stage bar (`stageBarKeyOf`). The API's SQL says the same (`browseStageSql`).
 */
function browseStageOf(row, now) {
    const windowStart = now.getTime() - exports.FINAL_DECISION_WINDOW_DAYS * DAY_MS;
    if (row.stage === 'rejected')
        return (row.closedAt ?? row.stageEnteredAt).getTime() >= windowStart ? 'rejected' : null;
    if (row.stage === 'new')
        return row.closedAt === null ? 'ranking' : null;
    const key = (0, stage_bar_1.stageBarKeyOf)(row);
    const tile = key === null ? null : (BAR_TO_BROWSE[key] ?? null);
    if (tile === 'selected')
        return row.stageEnteredAt.getTime() >= windowStart ? 'selected' : null;
    return tile;
}
exports.EXPERIENCE_BAND_VALUES = ['lt2', '2to4', '4to7', '7plus'];
// --- The query ---------------------------------------------------------------------------------
exports.BROWSE_SORTS = ['last_activity', 'newest', 'name', 'score'];
exports.BROWSE_SORT_LABELS = {
    last_activity: 'Last activity',
    newest: 'Newest first',
    name: 'Name',
    score: 'Score',
};
/**
 * CAND-01's filters. Every filter is optional and they combine with AND; options inside one
 * filter combine with OR, except skills, which follow `skillMode`. POOL-04's filters are all here.
 */
exports.candidateBrowseSchema = zod_1.z.object({
    /** Name (§L2 spellings), email, mobile, or a word in a title or an application answer. */
    text: zod_1.z.string().trim().max(200).optional(),
    /** Has a live application in one of these (Selected and Rejected: in the last 30 days). */
    stages: (0, pools_1.listParam)(zod_1.z.enum(exports.BROWSE_STAGES)),
    /** Applied to one of these jobs. */
    jobs: (0, pools_1.listParam)(schemas_1.uuidSchema),
    /** Carries one of these tags. */
    tags: (0, pools_1.listParam)(schemas_1.uuidSchema),
    /** "Starred by me". */
    starred: zod_1.z.enum(['me']).optional(),
    skills: (0, pools_1.listParam)(zod_1.z.string().trim().min(1).max(60)),
    skillMode: zod_1.z.enum(['all', 'any']).default('all'),
    experience: (0, pools_1.listParam)(zod_1.z.enum(exports.EXPERIENCE_BAND_VALUES)),
    /** At least this level. */
    education: zod_1.z.enum(enums_1.EDUCATION_LEVELS).optional(),
    /** Exact cities, as the facet lists them. */
    cities: (0, pools_1.listParam)(zod_1.z.string().trim().min(1).max(80)),
    /** Whole taka a month, as the career form asks. */
    salaryMin: zod_1.z.coerce.number().int().min(0).max(999_999_999).optional(),
    salaryMax: zod_1.z.coerce.number().int().min(0).max(999_999_999).optional(),
    maxNoticeDays: zod_1.z.coerce.number().int().min(0).max(365).optional(),
    /**
     * DECISIONS-LOG 2026-10-19 Q6: people with a live application whose ask is above that job's
     * budget top (same currency, `overBudgetPercent` > 0); with `jobs`, only those jobs count.
     * Refused for a viewer who may not see `candidate.expectedSalary` (#5).
     */
    overBudget: zod_1.z.enum(['yes']).optional(),
    /** "Last stage reached": where the person's latest application got to. */
    reached: (0, pools_1.listParam)(zod_1.z.enum(enums_1.APPLICATION_STAGES)),
    sources: (0, pools_1.listParam)(zod_1.z.string().trim().min(1).max(40)),
    /** Applied between (any application), Asia/Dhaka dates. */
    from: calendars_1.isoDateSchema.optional(),
    to: calendars_1.isoDateSchema.optional(),
    /** Added to the system between. */
    addedFrom: calendars_1.isoDateSchema.optional(),
    addedTo: calendars_1.isoDateSchema.optional(),
    /** Only while Talent pools is on (§L34); refused otherwise. */
    pools: (0, pools_1.listParam)(schemas_1.uuidSchema),
    pool: zod_1.z.enum(pools_1.POOL_FILTERS).optional(),
    /** Only while Custom fields is on. */
    customField: schemas_1.uuidSchema.optional(),
    customValue: zod_1.z.string().trim().min(1).max(200).optional(),
    sort: zod_1.z.enum(exports.BROWSE_SORTS).default('last_activity'),
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(100).default(50),
});
/** The facet call takes the same filters, plus a search inside the skill list. */
exports.candidateFacetsSchema = exports.candidateBrowseSchema.extend({
    skillSearch: zod_1.z.string().trim().max(60).optional(),
});
/** How many options a long facet (jobs, skills, cities, tags) lists before "Show more". */
exports.FACET_SHORT_LIST = 5;
exports.FACET_LONG_LIST = 30;
// --- Star and tags ------------------------------------------------------------------------------
exports.candidateTagSchema = zod_1.z
    .object({
    tagId: schemas_1.uuidSchema.optional(),
    /** A new tag's name; an existing tag with the same name (any case) is reused. */
    name: zod_1.z.string().trim().min(1, 'Name the tag.').max(40, 'Keep a tag under 40 characters.').optional(),
})
    .refine((value) => value.tagId !== undefined || value.name !== undefined, { message: 'Pick a tag or type a new one.', path: ['name'] });
/**
 * `message` is COMM-03's bulk send (F15.03) from the list: one email each, its variables filled in
 * for each person against their focus application. Messages can't be taken back, so never undoable (§L9).
 */
exports.CANDIDATE_BULK_ACTIONS = ['star', 'unstar', 'add_tag', 'remove_tag', 'message'];
/** A bulk action on ticked people, or on everyone matching the filters ("Select all N matching"). */
exports.candidateBulkSchema = zod_1.z
    .object({
    action: zod_1.z.enum(exports.CANDIDATE_BULK_ACTIONS),
    candidateIds: zod_1.z.array(schemas_1.uuidSchema).max(1000).optional(),
    /** The list's filters when the whole result was selected. */
    matching: exports.candidateBrowseSchema.omit({ sort: true, page: true, pageSize: true }).partial().optional(),
    tagId: schemas_1.uuidSchema.optional(),
    tagName: zod_1.z.string().trim().min(1).max(40).optional(),
    templateKey: zod_1.z.string().trim().max(120).nullable().optional(),
    subject: zod_1.z.string().trim().min(1, 'Write a subject.').max(communication_1.MESSAGE_SUBJECT_MAX).optional(),
    body: zod_1.z.string().trim().min(1, 'Write the message.').max(communication_1.MESSAGE_BODY_MAX).optional(),
})
    .refine((value) => (value.candidateIds !== undefined) !== (value.matching !== undefined), {
    message: 'Choose the people, or all that match.',
    path: ['candidateIds'],
})
    .refine((value) => (value.action !== 'add_tag' && value.action !== 'remove_tag') || value.tagId !== undefined || value.tagName !== undefined, {
    message: 'Pick a tag.',
    path: ['tagId'],
})
    .refine((value) => value.action !== 'message' || (value.subject !== undefined && value.body !== undefined), {
    message: 'Write the message.',
    path: ['body'],
});
/** The most people "Select all N matching" acts on at once. */
exports.CANDIDATE_BULK_MAX = 1000;
// --- The salary-over-budget mark (owner, 2026-10-17) -------------------------------------------
/**
 * How far an ask is above the job's budget top, in whole percent: (asks − top) ÷ top, rounded.
 * Null when it is within the budget, there is no budget, the ask is missing or the currencies
 * differ (amounts are never converted to compare, #7). 0 means above, but under 1%.
 */
function overBudgetPercent(asksMinor, asksCurrency, budgetMaxMinor, budgetCurrency) {
    if (asksMinor === null || budgetMaxMinor === null || budgetMaxMinor <= 0n)
        return null;
    if (asksCurrency !== null && budgetCurrency !== null && asksCurrency !== budgetCurrency)
        return null;
    if (asksMinor <= budgetMaxMinor)
        return null;
    const tenthsOfPercent = ((asksMinor - budgetMaxMinor) * 1000n) / budgetMaxMinor;
    return Math.round(Number(tenthsOfPercent) / 10);
}
/**
 * ADR 0061: whether an ask is at or under the job's budget top — `overBudgetPercent`'s rules, but telling "within" from
 * "not given". Null when there is no ask, no budget or the currencies differ.
 */
function expectedSalaryWithinBudget(asksMinor, asksCurrency, budgetMaxMinor, budgetCurrency) {
    if (asksMinor === null || budgetMaxMinor === null || budgetMaxMinor <= 0n)
        return null;
    if (asksCurrency !== null && budgetCurrency !== null && asksCurrency !== budgetCurrency)
        return null;
    return asksMinor <= budgetMaxMinor;
}
/** "25% over budget", or "Under 1% over budget" for an ask just above the top. */
function overBudgetLabel(percent) {
    return percent < 1 ? 'Under 1% over budget' : `${percent}% over budget`;
}
//# sourceMappingURL=candidate-browse.js.map